import { readUnhandledEnumCase } from 'rpc-client-utils';
import { chirpEnumToString } from 'academy-common-lib/utils/chirp';
import { isVideoAsset } from '../../tracks/tracksUtils';
import { PRE_ASSESSMENTS_CONTENT_PASS_THRESHOLD } from '../../../constants/PreAssessmentsConstants';
import { SUB_SCREENS } from '../../../constants/Tracking.migrated';
export const isPreAssessmentCompleted = quizState => quizState.generationStatus === 'COMPLETED' && quizState.totalQuestions > 0 && quizState.questionIndex >= quizState.totalQuestions && !quizState.isReviewingAnswer;
export const getFocusContentId = ({
  currentContentId,
  lessonContent,
  perContentScores
}) => {
  const startIndex = currentContentId ? lessonContent.findIndex(c => c.id === currentContentId) + 1 : 0;
  if (currentContentId && startIndex === 0) {
    return undefined;
  }
  for (let i = startIndex; i < lessonContent.length; ++i) {
    const contentId = lessonContent[i].id;
    const cachedScore = perContentScores[contentId];
    const score = cachedScore ? cachedScore.correct / cachedScore.total : undefined;
    if (score !== undefined && score <= PRE_ASSESSMENTS_CONTENT_PASS_THRESHOLD) {
      return contentId;
    }
  }
  return undefined;
};
const isContentInFocus = (preQuizState, contentId) => {
  const contentScore = preQuizState.perContentScores[contentId];
  if (!contentScore || contentScore.total === 0) {
    return false;
  }
  return contentScore.correct / contentScore.total <= PRE_ASSESSMENTS_CONTENT_PASS_THRESHOLD;
};
export const areAllAssessedVideosInFocus = ({
  lessonContent,
  preQuizState
}) => {
  if (!preQuizState || !isPreAssessmentCompleted(preQuizState)) {
    return false;
  }
  const {
    assessedVideoCount,
    allInFocus
  } = lessonContent.reduce((acc, content) => {
    if (!isVideoAsset(content)) {
      return acc;
    }
    const contentScore = preQuizState.perContentScores[content.id];
    if (!contentScore || contentScore.total === 0) {
      return acc;
    }
    return {
      assessedVideoCount: acc.assessedVideoCount + 1,
      allInFocus: acc.allInFocus && isContentInFocus(preQuizState, content.id)
    };
  }, {
    assessedVideoCount: 0,
    allInFocus: true
  });
  return assessedVideoCount > 0 && allInFocus;
};
export const showPreAssessmentFocusRibbon = ({
  contentId,
  lessonContent,
  preQuizState
}) => {
  if (!preQuizState || !isPreAssessmentCompleted(preQuizState)) {
    return false;
  }
  if (areAllAssessedVideosInFocus({
    lessonContent,
    preQuizState
  })) {
    return false;
  }
  return isContentInFocus(preQuizState, contentId);
};
export const derivePreQuizDisplayStatus = quizState => {
  if (!quizState) {
    return 'NOT_STARTED';
  }
  if (quizState.generationStatus === 'FAILED') {
    return 'ERROR';
  }
  if (!quizState.quizStarted) {
    return 'NOT_STARTED';
  }
  if (quizState.generationStatus !== 'COMPLETED') {
    return 'GENERATING';
  }
  if (isPreAssessmentCompleted(quizState)) {
    return 'COMPLETED';
  }
  return 'IN_PROGRESS';
};
export const getPreAssessmentTrackingProps = ({
  lesson,
  track,
  screen
}) => {
  var _ref, _ref2, _ref3, _ref4, _track$exam;
  const professorId = lesson.content.filter(isVideoAsset).flatMap(asset => asset.professors.map(p => p.id));
  const useCases = (_ref = lesson.useCases || (track === null || track === void 0 ? void 0 : track.useCases) || []) === null || _ref === void 0 ? void 0 : _ref.map(readUnhandledEnumCase);
  const businessObjectives = (_ref2 = lesson.businessObjectives || (track === null || track === void 0 ? void 0 : track.businessObjectives) || []) === null || _ref2 === void 0 ? void 0 : _ref2.map(readUnhandledEnumCase);
  const hsSoftwareFilters = (_ref3 = lesson.hsSoftwareFilters || (track === null || track === void 0 ? void 0 : track.hsSoftwareFilters) || []) === null || _ref3 === void 0 ? void 0 : _ref3.map(readUnhandledEnumCase);
  const subCategory = (_ref4 = lesson.subCategories || (track === null || track === void 0 ? void 0 : track.subCategories) || []) === null || _ref4 === void 0 ? void 0 : _ref4.map(readUnhandledEnumCase);
  const allCategories = subCategory;
  const contentLanguage = lesson.language ? readUnhandledEnumCase(lesson.language) : '';
  const visibility = chirpEnumToString(lesson.visibility || (track === null || track === void 0 ? void 0 : track.visibility));
  const creatorHubId = lesson.hubId || (track === null || track === void 0 ? void 0 : track.hubId);
  const sharedProps = {
    businessObjectives,
    contentLanguage,
    contentType: 'LESSON',
    creatorHubId,
    hsSoftwareFilters,
    lessonId: lesson.id,
    professorId,
    screen,
    subScreen: SUB_SCREENS.PRE_ASSESSMENT,
    trackId: track === null || track === void 0 ? void 0 : track.id,
    useCases
  };
  const pageViewProps = Object.assign({}, sharedProps, {
    allCategories,
    examId: track === null || track === void 0 || (_track$exam = track.exam) === null || _track$exam === void 0 ? void 0 : _track$exam.id,
    lessonName: lesson.name,
    trackName: track === null || track === void 0 ? void 0 : track.name
  });
  const interactionProps = Object.assign({}, sharedProps, {
    subCategory,
    title: lesson.name
  });
  const progressProps = Object.assign({}, sharedProps, {
    lessonName: lesson.name,
    subCategory,
    trackName: track === null || track === void 0 ? void 0 : track.name,
    visibility
  });
  return {
    pageViewProps,
    interactionProps,
    progressProps
  };
};